import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Log a gift card activity event
 * @param {string} userId - User ID
 * @param {string} type - Activity type
 * @param {Object} details - Gift card details
 */
const logGiftCardActivity = async (userId, type, details) => {
  try {
    await addDoc(collection(db, 'activity_logs'), {
      userID: userId,
      type,
      timestamp: serverTimestamp(),
      giftCard: {
        giftCardId: details.giftCardId,
        coinAmount: details.coinAmount,
        guaranteedPoints: details.guaranteedPoints,
        ...(details.code && { code: details.code }),
        ...(details.recipientEmail && { recipientEmail: details.recipientEmail }),
        ...(details.senderEmail && { senderEmail: details.senderEmail })
      }
    });
  } catch (error) {
    console.warn('Failed to log gift card activity:', error);
  }
};

/**
 * Fetch all gift cards for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of gift cards
 */
export const fetchUserGiftCards = async (userId) => {
  try {
    // Query for cards where user is the current owner
    const giftCardQuery = query(
      collection(db, 'giftCards'),
      where('owner', '==', userId)
    );
    const snapshot = await getDocs(giftCardQuery);

    const cards = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      activatedAt: doc.data().activatedAt?.toDate(),
      redeemedAt: doc.data().redeemedAt?.toDate(),
      transferredAt: doc.data().transferredAt?.toDate()
    }));

    // Sort cards: active cards first, then others. WEB-Q7 (Batch 3.3): the
    // legacy isRedeemed/isActivated fallbacks are gone - the Phase B export
    // confirmed production giftCards holds only digital-schema docs, which
    // always carry `state`.
    cards.sort((a, b) => {
      if (a.state === 'active' && b.state !== 'active') return -1;
      if (a.state !== 'active' && b.state === 'active') return 1;
      return 0;
    });

    return cards;
  } catch (error) {
    console.error('Error fetching user gift cards:', error);
    throw new Error('Failed to fetch gift cards');
  }
};

/**
 * Transfer a gift card to another permanent member
 * @param {string} giftCardId - Gift card document ID
 * @param {string} currentUserId - Current owner's user ID
 * @param {string} recipientEmail - Recipient's email address
 * @returns {Promise<Object>} Transfer result
 */
export const transferGiftCard = async (giftCardId, currentUserId, recipientEmail) => {
  try {
    const email = recipientEmail.trim().toLowerCase();

    // Find the recipient user (must be a permanent member)
    // Query by email first, then filter out anonymous users in code
    // This handles cases where isAnonymous field doesn't exist (permanent accounts)
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', email)
    );
    const usersSnapshot = await getDocs(usersQuery);

    if (usersSnapshot.empty) {
      throw new Error('Recipient not found. They must be a registered permanent member.');
    }

    // Filter out anonymous users (isAnonymous === true)
    const permanentUsers = usersSnapshot.docs.filter(doc => {
      const userData = doc.data();
      return userData.isAnonymous !== true; // Include if field doesn't exist or is false
    });

    if (permanentUsers.length === 0) {
      throw new Error('Recipient not found. They must be a registered permanent member.');
    }

    const recipientDoc = permanentUsers[0];
    const recipientUserId = recipientDoc.id;

    if (recipientUserId === currentUserId) {
      throw new Error('You cannot transfer a gift card to yourself.');
    }

    // Fetch gift card details for logging
    const giftCardDoc = await getDoc(doc(db, 'giftCards', giftCardId));
    
    if (!giftCardDoc.exists()) {
      throw new Error('Gift card not found.');
    }

    const giftCardData = giftCardDoc.data();

    // Validate that the gift card belongs to the current user
    if (giftCardData.owner !== currentUserId) {
      throw new Error('You can only transfer gift cards that you own.');
    }

    // Validate that the recipient is not already the current owner
    if (giftCardData.owner === recipientUserId) {
      throw new Error('You cannot transfer a gift card to its current owner.');
    }

    // Validate that the gift card is active (not redeemed)
    if (giftCardData.state !== 'active') {
      if (giftCardData.state === 'redeemed') {
        throw new Error('Redeemed gift cards cannot be transferred.');
      }
      throw new Error('Only active gift cards can be transferred.');
    }

    // Update the gift card
    await updateDoc(doc(db, 'giftCards', giftCardId), {
      owner: recipientUserId,
      transferredAt: serverTimestamp()
    });

    // Log for sender (transferred out)
    await logGiftCardActivity(currentUserId, 'gift_card_transferred_out', {
      giftCardId,
      coinAmount: giftCardData.coinAmount,
      guaranteedPoints: giftCardData.guaranteedPoints,
      code: giftCardData.code,
      recipientEmail: email
    });

    // Get sender's email for the activity log
    let senderEmail = null;
    try {
      const senderDoc = await getDoc(doc(db, 'users', currentUserId));
      if (senderDoc.exists()) {
        senderEmail = senderDoc.data().email;
      }
    } catch (error) {
      console.warn('Failed to fetch sender email for activity log:', error);
    }

    // Log for recipient (received)
    await logGiftCardActivity(recipientUserId, 'gift_card_received', {
      giftCardId,
      coinAmount: giftCardData.coinAmount,
      guaranteedPoints: giftCardData.guaranteedPoints,
      code: giftCardData.code,
      senderEmail: senderEmail
    });

    // Send notification email via cloud function (fire and forget - don't block on email)
    // This runs asynchronously so the UI can show success immediately
    (async () => {
      try {
        const functions = getFunctions();
        const sendNotification = httpsCallable(functions, 'sendGiftCardTransferNotification');
        await sendNotification({
          giftCardId,
          recipientEmail: email,
          senderEmail: senderEmail
        });
      } catch (emailError) {
        console.warn('Transfer notification email failed:', emailError);
        // Don't fail the transfer if email fails
      }
    })();

    return { success: true, recipientEmail: email };
  } catch (error) {
    console.error('Error transferring gift card:', error);
    throw new Error(error.message || 'Failed to transfer gift card');
  }
};

/**
 * Get display status for a gift card
 * @param {Object} giftCard - Gift card data
 * @returns {string} Status label
 */
export const getGiftCardStatus = (giftCard) => {
  // Map state to display status
  if (giftCard.state === 'redeemed') return 'Redeemed';
  if (giftCard.state === 'active') return 'Active';
  // Fallback for legacy data or unknown states
  return 'Inactive';
};
